// Thumbnail generator for Buffington Homes products

// Reusable filename sanitizer
export function sanitizeFilename(name) {
  // Windows + Unix illegal characters:  \ / : * ? " < > |
  // Also trim whitespace to avoid ugly surprises
  return String(name)
    .replace(/[\\\/:*?"<>|]/g, " ") 
    .trim();
}

/**
 * Generate thumbnail URL for a product
 * 
 * @param {number} id - Product ID
 * @param {string} specifier - Product specifier
 * @param {string} size - Image size (default: "256")
 * @param {number} focus - Focus value (default: 500)
 * @param {string} series - Series ID
 * @param {string} name - Product name (for custom thumbs)
 * @param {string} color - Product color (for custom thumbs)
 * @returns {string} Thumbnail URL
 */

export const generateThumbnail = (id, specifier, size = "256", focus = 500, series, name = '', color = '', options = {}) => {
  const product = {
    id: id,
    specifier: specifier,
    seriesID: series,
    name: name,
    color: color
  };

  // Series that use custom thumbnail files instead of render farm
  const customThumbSeries = [9723, 9724, 9725];

  // Bare RGB(r,g,b) specifiers need to be wrapped as COLOR(RGB(r,g,b)) for the
  // render farm to interpret them as a color swatch. Normalize once up front so
  // every branch below (including forceBaseThumb) uses the correct form.
  const rgbMatch = product.specifier && product.specifier.match(/^RGB\(\d+,\s*\d+,\s*\d+\)$/i);
  if (rgbMatch) {
    product.specifier = `COLOR(${product.specifier})`;
  }

  let thumbnail = `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?specifier=${product.specifier}&type=prod&extra=(focus=${focus})&Size=${size}`;

  // Force the render-farm base thumb (e.g. Wood Hood tab in Cabinets step —
  // wants the finish swatch, not the cabinet-door image). Skip the series-specific
  // custom-file mappings below.
  if (options.forceBaseThumb) {
    return thumbnail;
  }

  // Kitchen Faucets: custom thumbnails keyed by product ID
  if (parseInt(product.seriesID) === 9786) {
    return `/kitchen-faucets/${product.id}.jpg`;
  }

  // Cabinets: custom thumbnails keyed by product ID
  if (parseInt(product.seriesID) === 9785) {
    return `/cabinets/${product.id}.jpg`;
  }

  // Mirrors: custom thumbnails keyed by product ID
  if (parseInt(product.seriesID) === 9787) {
    return `/mirrors/${product.id}.jpg`;
  }

  if (customThumbSeries.includes(parseInt(product.seriesID))) {
    const imageName = sanitizeFilename(product.name+"-"+product.color);
    thumbnail = `/thumbs/${product.seriesID}/${imageName}.jpg`;
  }
  // Handle cabinet doors (series 9713)
  else if (product.seriesID == 9713) {
    thumbnail = `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?specifier=${product.specifier}&sel=6055643&type=base&extra=(focus=(@SIZE/@IMAGE[1][RES][X]))&Size=250&form=png`;
  }
  // Handle shutter profiles (series 9712)
  else if (product.seriesID == 9712) {
    thumbnail = `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?specifier=${product.specifier}&sel=6133437&type=base&extra=(focus=(@SIZE/@IMAGE[1][RES][X]))&Size=250&form=png`;
  }
  // Handle shutter colors (series 9711)
  else if (product.seriesID == 9711) {
    thumbnail = `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?specifier=${product.specifier}&type=prod&extra=(focus=${focus})&Size=${size}`;
  }
  // Handle exterior doors (series 9719)
  else if ([9719].includes(product.seriesID)) {
    thumbnail = `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?specifier=${product.specifier}&sel=6076403&size=150&extra=(RX=@apply[1][RES]%7C@image[1][RES],FOCUS=(@size[X]/@RX[X]/1in),size=(Y=(@size[X]*@RX[Y]/@RX[X])))`;
  }
  else if(product.seriesID == 9719){
    thumbnail = `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?specifier=${product.specifier}&sel=6076395&size=150&extra=(RX=@apply[1][RES]%7C@image[1][RES],FOCUS=(@size[X]/@RX[X]/1in),size=(Y=(@size[X]*@RX[Y]/@RX[X])))`;
  }


  return thumbnail;
};
